import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Palette, Save, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/apiClient";

const VOICES = ["Friendly", "Bold", "Professional", "Playful", "Inspirational", "Direct"];

const EMPTY = {
  business_name: "",
  niche: "",
  audience: "",
  core_offer: "",
  website: "",
  cta: "",
  brand_voice: "Friendly",
  signature_phrases: "",
  words_to_avoid: "",
  primary_color: "#1a2a6c",
  accent_color: "#00b09b",
  logo_url: "",
};

export default function BrandProfile() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/brand/logo", fd);
      setForm((f) => ({ ...f, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error("Could not upload logo");
    } finally {
      setUploading(false);
    }
  };

  const token = localStorage.getItem("h2i_token");
  const logoSrc = form.logo_url
    ? form.logo_url.startsWith("/api")
      ? `${API.replace(/\/api$/, "")}${form.logo_url}?auth=${token}`
      : form.logo_url
    : null;

  useEffect(() => {
    api.get("/brand").then((r) => {
      if (r.data) setForm({ ...EMPTY, ...r.data });
    });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/brand", form);
      toast.success("Brand profile saved");
    } catch (e) {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="brand-profile">
      <div className="fade-up flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          <Palette size={24} />
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold">Brand Profile</h1>
          <p className="text-muted-foreground">
            Saved once, reused across every course and campaign you generate.
          </p>
        </div>
      </div>

      <Card className="border-border p-8">
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Business / creator name">
            <Input data-testid="brand-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Bright Path Coaching" />
          </Field>
          <Field label="Niche">
            <Input data-testid="brand-niche" value={form.niche} onChange={set("niche")} placeholder="Personal finance for freelancers" />
          </Field>
          <Field label="Target audience">
            <Input data-testid="brand-audience" value={form.audience} onChange={set("audience")} placeholder="New freelancers earning their first $5k" />
          </Field>
          <Field label="Core offer (what you primarily sell)">
            <Input data-testid="brand-core-offer" value={form.core_offer} onChange={set("core_offer")} placeholder="$27 weekly meal-prep template pack" />
          </Field>
          <Field label="Website / link">
            <Input data-testid="brand-website" value={form.website} onChange={set("website")} placeholder="https://..." />
          </Field>
          <Field label="Call to action">
            <Input data-testid="brand-cta" value={form.cta} onChange={set("cta")} placeholder="Grab the free budget template" />
          </Field>
          <Field label="Brand voice">
            <Select value={form.brand_voice} onValueChange={(v) => setForm({ ...form, brand_voice: v })}>
              <SelectTrigger data-testid="brand-voice-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[...new Set([...VOICES, form.brand_voice].filter(Boolean))].map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Signature phrases">
            <Textarea data-testid="brand-phrases" value={form.signature_phrases} onChange={set("signature_phrases")} placeholder="Keep it simple, take one small step..." rows={3} />
          </Field>
          <Field label="Words to avoid">
            <Textarea data-testid="brand-avoid" value={form.words_to_avoid} onChange={set("words_to_avoid")} placeholder="hustle, grind, get-rich-quick" rows={3} />
          </Field>
          <Field label="Brand color (headlines & primary)">
            <div className="flex items-center gap-3">
              <input type="color" value={form.primary_color} onChange={set("primary_color")}
                data-testid="brand-color" className="h-10 w-14 cursor-pointer rounded-lg border border-border bg-transparent" />
              <Input value={form.primary_color} onChange={set("primary_color")} className="w-32" />
            </div>
          </Field>
          <Field label="Accent color (labels & highlights)">
            <div className="flex items-center gap-3">
              <input type="color" value={form.accent_color} onChange={set("accent_color")}
                data-testid="brand-accent-color" className="h-10 w-14 cursor-pointer rounded-lg border border-border bg-transparent" />
              <Input value={form.accent_color} onChange={set("accent_color")} className="w-32" />
            </div>
          </Field>
          <Field label="Logo">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-secondary">
                {logoSrc ? (
                  <img src={logoSrc} alt="logo" className="h-full w-full object-contain" data-testid="brand-logo-preview" />
                ) : (
                  <ImageIcon size={22} className="text-muted-foreground" />
                )}
              </div>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="brand-logo-input" />
                <span className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
                  <Upload size={15} /> {uploading ? "Uploading..." : "Upload logo"}
                </span>
              </label>
            </div>
          </Field>
        </div>

        <div className="mt-8 border-t border-border pt-6" data-testid="brand-preview-section">
          <p className="mb-3 text-sm font-medium text-muted-foreground">Live preview — how your shared deck & PDF will look</p>
          <div className="relative overflow-hidden rounded-2xl border border-border bg-white p-6 shadow-sm" data-testid="brand-preview">
            <div className="absolute inset-y-0 left-0 w-1.5" style={{ background: form.primary_color }} />
            <div className="pl-4">
              {logoSrc && <img src={logoSrc} alt="logo" className="mb-3 h-9 w-auto object-contain" data-testid="brand-preview-logo" />}
              <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: form.accent_color }}>Mini Course</span>
              <h3 className="mt-1 font-head text-2xl font-bold" style={{ color: form.primary_color }}>
                {form.business_name ? `${form.business_name} — Your Course` : "Your Course Title"}
              </h3>
              <p className="mt-2 max-w-xl text-sm text-[#57534E]">
                A beginner-friendly preview of your branding on every course output.
              </p>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wide"
                style={{ backgroundColor: `${form.accent_color}1a`, color: form.accent_color }}>
                Key callout
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="save-brand-btn"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            <Save size={18} className="mr-1" /> {saving ? "Saving..." : "Save profile"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
